import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  AddressSuggestion,
  PostalCodeLookupRequest,
  PostalCodeLookupResult,
} from './address-autocomplete.model';

const SUGGEST_TIMEOUT_MS = 3000;
const LOOKUP_TIMEOUT_MS = 5000;
const NO_MATCH_POSTAL_CODE = '00000';
// Alineado con el maxlength del input del código postal (alfanumérico, máx. 20)
const POSTAL_CODE_MAX_LENGTH = 20;
const ADDRESS_SEPARATOR = ',';

interface PostalCodeLookupResponse {
  result?: {
    postalCode?: string | null;
    matched?: { province?: unknown } | null;
    google?: { city?: unknown } | null;
  };
  google?: { province?: unknown } | null;
}

interface AutocompleteItem {
  placeId?: string;
  formattedAddress?: string;
  mainText?: string;
  secondaryText?: string;
}

interface AutocompleteResponse {
  message?: string;
  result?: AutocompleteItem[];
}

@Injectable({ providedIn: 'root' })
export class AddressAutocompleteService {
  // El JWT lo agrega AuthInterceptor
  constructor(private http: HttpClient) {}

  suggest(input: string, country: string): Observable<AddressSuggestion[]> {
    const params = new HttpParams().set('input', input).set('includedRegionCodes', country);

    return this.http
      .get<AutocompleteResponse>(`${environment.backendUrl}/api/v1/google-places/autocomplete`, { params })
      .pipe(
        timeout(SUGGEST_TIMEOUT_MS),
        map((response) => this.toSuggestions(response)),
        catchError(() => of<AddressSuggestion[]>([])),
      );
  }

  // TODO: enviar request.placeId cuando el backend lo soporte
  lookupPostalCode(
    request: PostalCodeLookupRequest,
    country: string,
  ): Observable<PostalCodeLookupResult | null> {
    const params = new HttpParams().set('textQuery', request.description).set('regionCode', country);

    return this.http
      .get<PostalCodeLookupResponse>(`${environment.backendUrl}/api/v1/postal-codes/lookup`, { params })
      .pipe(
        timeout(LOOKUP_TIMEOUT_MS),
        map((response) => this.toLookupResult(response, country)),
        catchError(() => of(null)),
      );
  }

  private toLookupResult(
    response: PostalCodeLookupResponse | null,
    countryCode: string,
  ): PostalCodeLookupResult | null {
    const rawPostalCode = response?.result?.postalCode?.trim();
    const postalCode =
      rawPostalCode && rawPostalCode !== NO_MATCH_POSTAL_CODE && rawPostalCode.length <= POSTAL_CODE_MAX_LENGTH
        ? rawPostalCode
        : undefined;
    const province =
      this.readNonEmptyString(response?.result?.matched?.province) ??
      this.readNonEmptyString(response?.google?.province);
    const city = this.readNonEmptyString(response?.result?.google?.city);
    if (!postalCode && !province && !city) return null;
    return { postalCode, countryCode, province, city };
  }

  private readNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' ? value.trim() || undefined : undefined;
  }

  private toSuggestions(response: AutocompleteResponse | null): AddressSuggestion[] {
    if (!Array.isArray(response?.result)) return [];
    return response.result
      .filter((item) => !!item?.formattedAddress)
      .map((item) => {
        const description = item.formattedAddress as string;
        const separatorIndex = description.indexOf(ADDRESS_SEPARATOR);
        const hasSeparator = separatorIndex >= 0;
        return {
          placeId: item.placeId,
          description,
          mainText: this.readNonEmptyString(item.mainText) ?? (hasSeparator ? description.slice(0, separatorIndex).trim() : description),
          secondaryText: item.secondaryText ?? (hasSeparator ? description.slice(separatorIndex + 1).trim() : ''),
        };
      });
  }
}
